// Natively/OneSignal bridge access (native Android wrapper).
//
// Split out of use-onesignal-identity.ts so non-hook code — specifically the
// SESSION_REVOKED interceptor in lib/queryClient.ts — can unbind the device
// without importing a hook module that itself imports queryClient (cycle).
// Outside the native app every function is a no-op.
//
// Bridge surface VERIFIED against the Natively SDK primary source
// (github.com/buildnatively/js-sdk, src/classes/NativelyNotifications.ts;
// vendored bundle: client/public/natively-frontend.min.js, natively@2.28.7,
// ISC license):
//   setExternalId({ externalId }, callback)  — standard alias (not JWT verified)
//   removeExternalId(callback)               — unbind it
//   requestPermission(fallbackToSettings, callback)
//   getPermissionStatus(callback)
//   getOneSignalId(callback)                 — device subscription id
//
// SDK loading: the SDK is NOT bundled — it assigns window globals for every
// visitor. Instead `ensureNativelySdkLoaded()` injects the vendored script
// only when the native markers are present, so plain browsers never load it.
// Native detection follows the SDK's own NativelyInfo.browserInfo():
// `window.$agent` exists only inside the wrapper (plus the Natively UA
// markers as a fallback).
import { NATIVE_PUSH_IDENTITY_SUPPORTED } from "@shared/native-push-policy";

type NativelyCallback = (response: unknown) => void;

type NativelyNotificationsBridge = {
  setExternalId?: (data: { externalId?: string }, callback: NativelyCallback) => void;
  removeExternalId?: (callback: NativelyCallback) => void;
  requestPermission?: (fallbackToSettings: boolean, callback: NativelyCallback) => void;
  getPermissionStatus?: (callback: NativelyCallback) => void;
  getOneSignalId?: (callback: NativelyCallback) => void;
};

declare global {
  interface Window {
    natively?: unknown;
    $agent?: unknown;
    NativelyNotifications?: new () => NativelyNotificationsBridge;
  }
}

const SDK_SCRIPT_ID = "natively-sdk";
const SDK_SCRIPT_SRC = "/natively-frontend.min.js";
// The wrapper injects $agent before page scripts run, so the class appears as
// soon as the script executes; the poll only bridges script-load latency.
const SDK_POLL_INTERVAL_MS = 100;
const SDK_POLL_TIMEOUT_MS = 10_000;

export function isInsideNativeApp(): boolean {
  if (typeof window === "undefined") return false;
  if (typeof window.$agent !== "undefined") return true;
  const ua = window.navigator?.userAgent ?? "";
  return ua.includes("Natively/Android") || ua.includes("Natively/iOS");
}

let sdkLoadPromise: Promise<boolean> | null = null;

/**
 * Load the vendored Natively SDK when (and only when) running inside the
 * native wrapper. Resolves true once window.NativelyNotifications exists.
 * Idempotent: concurrent callers share one promise, and an already-present
 * class (e.g. the script was added to the HTML shell later) short-circuits.
 */
export function ensureNativelySdkLoaded(): Promise<boolean> {
  if (typeof window === "undefined" || !isInsideNativeApp()) return Promise.resolve(false);
  if (typeof window.NativelyNotifications === "function") return Promise.resolve(true);
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise<boolean>((resolve) => {
    try {
      if (!document.getElementById(SDK_SCRIPT_ID)) {
        const script = document.createElement("script");
        script.id = SDK_SCRIPT_ID;
        script.src = SDK_SCRIPT_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
    } catch {
      console.error("[onesignal-identity] SDK injection failed");
    }
    // Poll for the global class rather than relying on script.onload — it
    // also covers the script having been injected by other means, and makes
    // delayed availability deterministic to test.
    const start = Date.now();
    const timer = setInterval(() => {
      if (typeof window.NativelyNotifications === "function") {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - start > SDK_POLL_TIMEOUT_MS) {
        clearInterval(timer);
        console.error("[onesignal-identity] Natively SDK did not become available");
        // Allow a later retry (e.g. after connectivity returns).
        sdkLoadPromise = null;
        resolve(false);
      }
    }, SDK_POLL_INTERVAL_MS);
  });
  return sdkLoadPromise;
}

// Bridge instance is memoized once constructed (the SDK opens a channel to
// the native layer per instance). Deliberately NOT memoized while null: the
// SDK may still be loading, and caching the miss would blind every later
// call.
let bridge: NativelyNotificationsBridge | null = null;

function getBridge(): NativelyNotificationsBridge | null {
  if (bridge) return bridge;
  if (typeof window === "undefined") return null;
  if (typeof window.NativelyNotifications !== "function") return null;
  if (!isInsideNativeApp()) return null;
  try {
    bridge = new window.NativelyNotifications();
  } catch {
    console.error("[onesignal-identity] bridge construction failed");
  }
  return bridge;
}

/** True when running inside the Natively wrapper with the OneSignal bridge. */
export function isNativePushAvailable(): boolean {
  return getBridge() !== null;
}

let desiredExternalId: string | null = null;
let boundExternalId: string | null = null;
let bindInFlight = false;
let identityGeneration = 0;

export function getBoundExternalId(): string | null {
  return boundExternalId;
}

export function bindOneSignalIdentity(externalId: string): boolean {
  if (!isInsideNativeApp() || !NATIVE_PUSH_IDENTITY_SUPPORTED ||
      !/^host-[1-9]\d*$/.test(externalId) ||
      !Number.isSafeInteger(Number(externalId.slice(5)))) return false;
  if (desiredExternalId === externalId && (boundExternalId === externalId || bindInFlight || unbindPending)) return true;
  desiredExternalId = externalId;
  identityGeneration++;
  queueIdentityRemoval();
  return true;
}

function queueIdentityRemoval(): void {
  boundExternalId = null;
  unbindPending = true;
  storeUnbindPending(true);
  void flushUnbind();
}

function flushBind(): void {
  if (bindInFlight || unbindPending || !desiredExternalId) return;
  const b = getBridge();
  if (typeof b?.setExternalId !== "function") {
    unbindPending = true;
    retryUnbind();
    return;
  }
  const id = desiredExternalId;
  const generation = identityGeneration;
  bindInFlight = true;
  let settled = false;
  const finish = (ok: boolean) => {
    if (settled) {
      // A timed-out native call can still complete later. Clear it again,
      // then reconcile the CURRENT desired account, never the captured one.
      queueIdentityRemoval();
      return;
    }
    settled = true;
    clearTimeout(timer);
    bindInFlight = false;
    if (generation !== identityGeneration || desiredExternalId !== id || unbindPending) {
      queueIdentityRemoval();
    } else if (ok) {
      boundExternalId = id;
      ensurePushPermissionOnce();
    } else {
      console.error("[onesignal-identity] native login unconfirmed; retrying");
      unbindPending = true;
      storeUnbindPending(true);
      retryUnbind();
    }
  };
  const timer = setTimeout(() => finish(false), CALLBACK_TIMEOUT_MS);
  try {
    b.setExternalId({ externalId: id }, response => {
      const value = response as { externalId?: unknown; error?: unknown; message?: unknown } | null;
      finish(!!value && value.externalId === id && !value.error && !value.message);
    });
  } catch { finish(false); }
}

const UNBIND_PENDING_KEY = "padpulse:native-push-unbind-pending-v1";
const SESSION_CLEARED_KEY = "padpulse:native-push-session-cleared-v1";
const CALLBACK_TIMEOUT_MS = 5_000;
const UNBIND_RETRY_MS = 5_000;
let unbindPending = false;
let unbindInFlight = false;
let retryTimer: ReturnType<typeof setTimeout> | undefined;

function storeUnbindPending(pending: boolean): void {
  try {
    if (pending) localStorage.setItem(UNBIND_PENDING_KEY, "1");
    else localStorage.removeItem(UNBIND_PENDING_KEY);
  } catch {
    // The in-memory queue still works when WebView storage is unavailable.
  }
}

function retryUnbind(): void {
  if (retryTimer || !unbindPending) return;
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    void flushUnbind();
  }, UNBIND_RETRY_MS);
}

async function flushUnbind(): Promise<void> {
  if (!unbindPending || unbindInFlight) return;
  unbindInFlight = true;
  if (!await ensureNativelySdkLoaded()) {
    unbindInFlight = false;
    retryUnbind();
    return;
  }
  const b = getBridge();
  if (typeof b?.removeExternalId !== "function") {
    unbindInFlight = false;
    console.error("[onesignal-identity] native identity removal unavailable");
    retryUnbind();
    return;
  }

  // Natively documents null/empty as successful removal; error/message are
  // failures. Do not optimistically acknowledge dispatch, accept arbitrary
  // objects, or print native callback payloads (they may contain identities).
  let settled = false;
  const finish = (ok: boolean) => {
    if (settled) {
      queueIdentityRemoval();
      return;
    }
    settled = true;
    clearTimeout(timeout);
    unbindInFlight = false;
    if (ok) {
      unbindPending = false;
      storeUnbindPending(false);
      flushBind();
    } else {
      console.error("[onesignal-identity] native identity removal unconfirmed; retrying");
      retryUnbind();
    }
  };
  const timeout = setTimeout(() => finish(false), CALLBACK_TIMEOUT_MS);
  try {
    b.removeExternalId((response) => {
      const empty = response == null || response === "" ||
        (typeof response === "object" && !Array.isArray(response) &&
          Object.keys(response).length === 0);
      finish(empty);
    });
  } catch {
    finish(false);
  }
}

/**
 * Unbind this device from its OneSignal external id. Safe and idempotent to
 * call anywhere (no-op outside the native wrapper). Wired into the logout
 * mutations, the SESSION_REVOKED interceptor, and the identity hook's 401
 * path — always unconditionally, because a persisted binding from a previous
 * app launch is invisible to this page's bookkeeping.
 */
export function logoutOneSignalIdentity(): void {
  if (!isInsideNativeApp()) return;
  desiredExternalId = null;
  identityGeneration++;
  try { localStorage.setItem(SESSION_CLEARED_KEY, String(Date.now())); } catch {}
  // Own the queue independently of React. A logout followed by navigation or
  // unmount must still reach a bridge that becomes available later.
  queueIdentityRemoval();
}

// Android 13+ shows the push-permission dialog at most twice, and repeated
// programmatic prompting is hostile UX — so the app asks exactly once per
// install, after the signed-in account's external-ID callback succeeds.
// If configured to auto-prompt
// on launch, the native layer no-ops an already-decided permission, so this
// stays safe either way. Denied permission never blocks the app — the device
// simply receives no pushes until the user enables them in system settings.
const PERMISSION_PROMPTED_KEY = "padpulse:native-push-prompt-v1";
let permissionPrompted = false;

export function ensurePushPermissionOnce(): void {
  const b = getBridge();
  if (!b) return;
  try {
    if (permissionPrompted || typeof b.requestPermission !== "function") return;
    let alreadyPrompted = false;
    try {
      alreadyPrompted = localStorage.getItem(PERMISSION_PROMPTED_KEY) === "1";
    } catch {}
    if (alreadyPrompted) return;
    permissionPrompted = true;
    try {
      localStorage.setItem(PERMISSION_PROMPTED_KEY, "1");
    } catch {}
    // Never repeat a denied/failed prompt, even if storage is unavailable.
    b.requestPermission(false, () => {});
  } catch {
    console.error("[onesignal-identity] permission flow failed");
  }
}

// A cold launch can land on the public login page without AppLayout mounted.
// Clear any persisted native alias before that page can retain the last account.
if (typeof window !== "undefined") {
  window.addEventListener("storage", event => {
    if (event.key === SESSION_CLEARED_KEY && event.newValue) {
      // Do not echo this event back to other WebViews. A fresh auth response
      // must establish the desired account again before the next bind.
      desiredExternalId = null;
      identityGeneration++;
      queueIdentityRemoval();
    }
  });
  if (isInsideNativeApp()) logoutOneSignalIdentity();
}
